import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Eye, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-white dark:bg-[#030014] pt-24 pb-12">
            <div className="container mx-auto px-6 max-w-4xl">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-10"
                >
                    <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-2xl text-primary mb-4 md:mb-6">
                        <ShieldCheck size={28} />
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-4 md:mb-6">
                        Privacy <span className="text-gradient">Policy</span>
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 text-base md:text-lg">
                        Last updated: Feb 10, 2024
                    </p>
                </motion.div>

                {/* Content Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="prose prose-slate dark:prose-invert max-w-none space-y-8 md:space-y-12"
                >
                    <section className="bg-slate-50 dark:bg-white/5 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 dark:border-white/5">
                        <div className="flex items-center gap-4 mb-4 md:mb-6">
                            <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/20 rounded-xl flex items-center justify-center text-primary">
                                <Eye size={18} />
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold m-0 text-slate-900 dark:text-white">Introduction</h2>
                        </div>
                        <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                            At MehareTech Solutions, we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website and tell you about your privacy rights.
                        </p>
                    </section>

                    <section className="space-y-4 md:space-y-6">
                        <div className="flex items-center gap-4 mb-4 md:mb-6">
                            <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/20 rounded-xl flex items-center justify-center text-primary">
                                <Lock size={18} />
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold m-0 text-slate-900 dark:text-white">Data We Collect</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div className="p-5 md:p-6 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5">
                                <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3 text-primary">Personal Information</h3>
                                <p className="text-[13px] md:text-sm text-slate-600 dark:text-slate-400">Name, email address, phone number, and company details provided via contact forms.</p>
                            </div>
                            <div className="p-5 md:p-6 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5">
                                <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3 text-primary">Usage Data</h3>
                                <p className="text-[13px] md:text-sm text-slate-600 dark:text-slate-400">IP address, browser type, pages visited, and time spent on our platform.</p>
                            </div>
                        </div>
                    </section>

                    <section className="bg-slate-50 dark:bg-white/5 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 dark:border-white/5">
                        <div className="flex items-center gap-4 mb-4 md:mb-6">
                            <div className="w-9 h-9 md:w-10 md:h-10 bg-primary/20 rounded-xl flex items-center justify-center text-primary">
                                <FileText size={18} />
                            </div>
                            <h2 className="text-xl md:text-2xl font-bold m-0 text-slate-900 dark:text-white">How We Use Your Data</h2>
                        </div>
                        <ul className="space-y-3 md:space-y-4 text-sm text-slate-600 dark:text-slate-400">
                            <li className="flex gap-3 items-start">
                                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></span>
                                To provide and maintain our Service, including to monitor usage.
                            </li>
                            <li className="flex gap-3 items-start">
                                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></span>
                                To contact you regarding updates, offers, or inquiries.
                            </li>
                            <li className="flex gap-3 items-start">
                                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0"></span>
                                To improve performance and user experience.
                            </li>
                        </ul>
                    </section>

                    <footer className="text-center pt-10">
                        <p className="text-slate-500 dark:text-slate-500 text-sm">
                            If you have any questions about this Privacy Policy, you can contact us at:<br />
                            <span className="text-primary font-bold">meharetech@gmail.com</span>
                        </p>
                    </footer>
                </motion.div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
